import { Trans } from '~/common/context';
import {
  Button,
  Col,
  Flex,
  Icon,
  IconButton,
  Text,
  useConfiguredForm,
  DndProvider,
  DragHandle,
  SortableItem,
} from '@wyz/ui';
import clsx from 'clsx';
import * as React from 'react';
import { useFormContext } from 'react-hook-form';
import { SupplierButton } from '~/connect/components';
import { SUPPLIERS_STATUSES } from '~/connect/schemas';
import { ISupplierTemplateForm } from '~/connect/types';
import { SUPPLIERS_TYPES_IDS } from '@wyz/utils';

import { IFeatureContext, ISupplierTemplateSupplier } from '~/common/types';
import { FEATURE_CONTEXT } from '~/common/schemas';

export type SupplierItem = Omit<
  Partial<ISupplierTemplateSupplier>,
  'extendedManufConfiguration'
> & {
  wyzApiId: number;
  type: number;
  status: string;
  extendedManufConfiguration?: {
    enabled?: boolean | null;
    warehouseId?: string | null;
    displayName?: string | null;
  } | null;
  enabled?: boolean;
  customerId?: string | null;
  _internalStock?: boolean;
};

interface Props {
  featureContext: IFeatureContext;
  suppliers?: SupplierItem[];
  onMove: (indexA: number, indexB: number) => void;
  onConfigure: (selected: SupplierItem) => void;
  onRemove: (id: number) => void;
  onConfigureBestSourcing: (index: number) => void;
  canDrag?: (supplier: SupplierItem) => boolean;
  canRemove?: (supplier: SupplierItem) => boolean;
}

const SupplierDraggables: React.FC<Props> = ({
  featureContext,
  suppliers = [],
  onConfigure,
  onMove,
  onRemove,
  onConfigureBestSourcing,
  canDrag = () => true,
  canRemove = () => true,
}) => {
  const configurationContext = useConfiguredForm();
  const methods = useFormContext<ISupplierTemplateForm>();

  return (
    <DndProvider
      items={suppliers}
      onMove={onMove}
      getId={(item) => item.wyzApiId}
      renderItem={(supplier, index) => {
        const isDraggable = canDrag(supplier);
        const isRemovable = canRemove(supplier);

        return (
          <SortableItem id={supplier.wyzApiId}>
            <div className='d-flex align-items-center'>
              <Col md={1}>
                <Text heading='normal' color='tertiary' weight='bold'>
                  {index + 1}
                </Text>
              </Col>
              <Col
                md={11}
                className={clsx({
                  'rounded border border-2 border-red': Boolean(
                    methods.formState.errors.suppliers?.[index] ||
                    (methods.formState.errors
                      .bestSourcingSupplierConfigurations &&
                      supplier.type === SUPPLIERS_TYPES_IDS.BEST_SOURCING),
                  ),
                })}
              >
                <SupplierButton>
                  <Flex alignItems='center' justifyContent='between'>
                    {supplier.status === SUPPLIERS_STATUSES.DISABLED ? (
                      <div className='opacity-50'>
                        <span
                          className='ms-4'
                          id={`supplier-name-${supplier.wyzApiId}`}
                        >
                          {supplier.name}{' '}
                          <Trans i18nKey='app.supplier_template.form.suppliers.inactive_status' />
                        </span>
                      </div>
                    ) : (
                      <span
                        className='ms-4'
                        id={`supplier-name-${supplier.wyzApiId}`}
                      >
                        {supplier.name}
                      </span>
                    )}
                    <div
                      className='hoverable-btn position-absolute'
                      style={{ right: 0 }}
                    >
                      {configurationContext.mode === 'READ_ONLY' && (
                        <IconButton
                          id={`detail-${supplier.name}`}
                          icon='eye'
                          variant='tertiary-blue'
                          size='sm'
                          iconSize='sm'
                          onClick={() =>
                            supplier.type ===
                              SUPPLIERS_TYPES_IDS.BEST_SOURCING &&
                            featureContext !==
                              FEATURE_CONTEXT.REDISTRIBUTION_SUPPLIER_TEMPLATE
                              ? onConfigureBestSourcing(index)
                              : onConfigure(supplier)
                          }
                        />
                      )}
                      {configurationContext.mode !== 'READ_ONLY' && (
                        <div className='space-x-2 d-flex bg-neutral-grey-2 align-items-center'>
                          {supplier.type ===
                            SUPPLIERS_TYPES_IDS.BEST_SOURCING &&
                          featureContext !==
                            FEATURE_CONTEXT.REDISTRIBUTION_SUPPLIER_TEMPLATE ? (
                            <IconButton
                              id='configure-best-sourcing'
                              variant='tertiary-blue'
                              icon='settings'
                              onClick={() => onConfigureBestSourcing(index)}
                            />
                          ) : (
                            <Button
                              id={`configure-${supplier.name}`}
                              variant='tertiary-blue'
                              size='sm'
                              onClick={() => onConfigure(supplier)}
                            >
                              <Flex alignItems='center' className='space-x-1'>
                                <Icon icon='edit' width={15} height={15} />
                                <span>
                                  <Trans i18nKey='app.common.button.configure' />
                                </span>
                              </Flex>
                            </Button>
                          )}

                          {isRemovable && (
                            <Button
                              id={`remove-${supplier.name}`}
                              variant='tertiary-grey'
                              size='sm'
                              onClick={() => onRemove(supplier.wyzApiId)}
                            >
                              <Icon icon='minus' width={20} height={20} />
                              <span>
                                <Trans i18nKey='app.common.buttons.remove' />
                              </span>
                            </Button>
                          )}
                          {isDraggable && (
                            <DragHandle id={`drag-${supplier.name}`} />
                          )}
                        </div>
                      )}
                    </div>
                  </Flex>
                </SupplierButton>
              </Col>
            </div>
          </SortableItem>
        );
      }}
    ></DndProvider>
  );
};
export default SupplierDraggables;
